import React, { useEffect, useState } from "react";
import Modal from "react-modal";

import styles from "./Drive.module.scss";
import { htmlToMarkdown, markdownToHtml } from "./markdown";

/**
 * Add or edit one Drive item: a title, the folder it lives in, and the text.
 *
 * The text is Markdown. Pasting from Google Docs or Word converts the pasted
 * formatting — headings, bold, italics, lists, tables — into Markdown at the
 * cursor, instead of the flat run of text a textarea would otherwise keep.
 * Preview shows it the way the Drive page will.
 *
 * Saving an edit sends the `version` that was opened, so if someone else saved
 * in the meantime the API refuses rather than overwriting their work — the
 * message says to reload, and nothing typed here is lost until the modal is
 * closed.
 */
const ItemEditor = ({ isOpen, item, folders, defaultFolderId, onSave, onClose }) => {
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("write");

  useEffect(() => {
    if (!isOpen) return;
    setTitle(item?.title || "");
    setFolderId(item?.folderId || defaultFolderId || folders[0]?._id || "");
    setContent(item?.content || "");
    setSaving(false);
    setTab("write");
  }, [isOpen, item, defaultFolderId, folders]);

  const dirty =
    !item ||
    title.trim() !== item.title ||
    content !== item.content ||
    String(folderId) !== String(item.folderId);

  const submit = async () => {
    if (!title.trim() || !folderId || saving) return;
    setSaving(true);
    try {
      await onSave({ title: title.trim(), folderId, content, ...(item ? { version: item.version } : {}) });
    } finally {
      setSaving(false);
    }
  };

  // Formatted paste (Docs, Word, a web page) → Markdown at the cursor. Plain
  // text pastes are left to the browser, so undo still works for them.
  const handlePaste = (event) => {
    const html = event.clipboardData?.getData("text/html");
    if (!html) return;
    const markdown = htmlToMarkdown(html);
    if (!markdown) return;
    event.preventDefault();
    const area = event.target;
    const start = area.selectionStart ?? content.length;
    const end = area.selectionEnd ?? content.length;
    const next = content.slice(0, start) + markdown + content.slice(end);
    setContent(next);
    requestAnimationFrame(() => {
      area.selectionStart = area.selectionEnd = start + markdown.length;
    });
  };

  // A long prompt is real work; closing on a stray click outside would lose it.
  const close = () => {
    if (saving) return;
    if (dirty && (title || content) && !window.confirm("Discard your changes?")) return;
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={close} className="modal_body_md" overlayClassName="modal_overlay">
      <div className="box_wrapper">
        <h4 className="mb-3">{item ? "Edit item" : "New item"}</h4>

        <div className="row">
          <div className="col-sm-8 mb-3">
            <label className="label">Title</label>
            <input
              className="form_control"
              placeholder="e.g. Overview prompt"
              value={title}
              maxLength={120}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="col-sm-4 mb-3">
            <label className="label">Folder</label>
            <select className="form_control" value={folderId} onChange={(e) => setFolderId(e.target.value)}>
              {folders.map((folder) => (
                <option key={folder._id} value={folder._id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-12 mb-2">
            <div className="d-flex align-items-center justify-content-between">
              <label className="label">Content (Markdown)</label>
              <div className={styles.tabs}>
                {["write", "preview"].map((name) => (
                  <button
                    key={name}
                    type="button"
                    className={tab === name ? styles.tabActive : undefined}
                    onClick={() => setTab(name)}
                  >
                    {name === "write" ? "Write" : "Preview"}
                  </button>
                ))}
              </div>
            </div>
            {tab === "write" ? (
              <textarea
                className="form_control"
                // Markdown is read as written — monospace keeps indentation,
                // list markers and blank lines honest.
                style={{ minHeight: 360, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 }}
                placeholder="Paste from Google Docs or Word — headings, bold and lists are kept as Markdown."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onPaste={handlePaste}
              />
            ) : (
              <div
                className={`${styles.markdown} ${styles.previewBox}`}
                // Sanitised in markdownToHtml.
                dangerouslySetInnerHTML={{ __html: markdownToHtml(content) || "<p>(empty)</p>" }}
              />
            )}
            <small className="text-muted">
              {content.length.toLocaleString()} characters
              {item ? ` · saving creates version ${item.version + 1}; earlier versions stay in History` : ""}
            </small>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary" onClick={close} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-dark"
            onClick={submit}
            disabled={saving || !title.trim() || !folderId || !dirty}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ItemEditor;
